/**
 * pages/Legal.tsx — the five legally-required documents.
 *
 *   Row 4: Prepaid account agreement   (CFPB Prepaid Rule)
 *   Row 5: Privacy policy               (App Store, Reg P)
 *   Row 6: Terms of service             (Standard)
 *   Row 7: Fee schedule (long form)     (CFPB Prepaid Rule)
 *   Row 8: Your Reg E rights            (Reg E consumer disclosures)
 *
 * Route: /legal/:slug
 *
 * Content policy:
 *   The final legal copy is being drafted by counsel. Until it's
 *   finalised each screen shows a plain, honest banner saying so,
 *   PLUS a short skeleton outline of the sections that will appear.
 *   The skeleton is enough for an App Store reviewer or a regulator
 *   to see the row is real, and for the user to know what will be
 *   there. When the final copy lands each entry gets its "sections"
 *   array replaced with the actual text.
 *
 * These pages sit INSIDE AppShell so the primary nav stays consistent
 * — but they're linked to publicly-reachable URLs so counsel can send
 * a regulator a plain link without an account. Because the app requires
 * a session, the protected wrapper will bounce anonymous visitors to
 * /signin — that's the same behaviour as the mobile app and is the
 * conventional pattern for embedded legal documents. The
 * out-of-session-readable versions live on the marketing site.
 */

import { useParams } from "react-router-dom";
import { Card } from "../components";
import { AppShell } from "./AppShell";
import styles from "./Legal.module.css";

// ---------------------------------------------------------------------------
// Legal document model.
// ---------------------------------------------------------------------------

interface LegalSection {
  heading: string;
  body: string;
}

interface LegalDoc {
  slug: string;
  eyebrow: string;
  title: string;
  requirement: string;
  effective: string;
  intro: string;
  sections: LegalSection[];
}

// ---------------------------------------------------------------------------
// The five documents.
// ---------------------------------------------------------------------------

const DOCS: Record<string, LegalDoc> = {
  "prepaid-account-agreement": {
    slug: "prepaid-account-agreement",
    eyebrow: "Legal",
    title: "Prepaid account agreement",
    requirement:
      "CFPB Prepaid Accounts Rule (12 CFR §1005.18) — filed with the CFPB and posted publicly.",
    effective: "Effective date: to be set upon partner-bank agreement.",
    intro:
      "This agreement governs your AsterCash prepaid account. It is written in plain language wherever the law permits and is available in the same form on our public website. A copy is on file with the Consumer Financial Protection Bureau.",
    sections: [
      {
        heading: "1. Who this agreement is between",
        body:
          "This agreement is between you (the account holder) and [Sponsor Bank], N.A., Member FDIC, which holds your funds. AsterCash operates the service under a program-manager agreement with [Sponsor Bank].",
      },
      {
        heading: "2. Opening your account",
        body:
          "By opening an AsterCash account you certify the information you provided is accurate and that you are authorised to open the account. We verify identity at account opening as required by the Bank Secrecy Act.",
      },
      {
        heading: "3. Fees",
        body:
          "The full fee schedule is available at /legal/fee-schedule. In summary: there is no monthly maintenance fee, no fee to send money to another AsterCash user, no fee to receive money, and no fee to add money from a linked bank account by ACH. Third-party fees may apply for card top-ups.",
      },
      {
        heading: "4. FDIC insurance",
        body:
          "Funds held in your AsterCash account are held at [Sponsor Bank], N.A., Member FDIC, and are insured up to $250,000 per account holder in aggregate with other funds you may hold at [Sponsor Bank].",
      },
      {
        heading: "5. Your rights under Regulation E",
        body:
          "Your rights when there is an error on your account are described in full at /legal/reg-e-rights. Notify us within 60 days of the statement showing the error.",
      },
      {
        heading: "6. Closing your account",
        body:
          "You may close your account at any time under Settings → Close account. Remaining balance will be returned to a linked funding source or by check within 10 business days. See Section 7 of the privacy policy for what happens to your data.",
      },
      {
        heading: "7. Changes to this agreement",
        body:
          "We will notify you at least 21 days before any change that materially affects your rights or increases your costs. Notice is delivered to the email address on your account.",
      },
      {
        heading: "8. Governing law and dispute resolution",
        body:
          "This agreement is governed by federal law and, to the extent state law applies, the laws of the state of [State], without regard to conflict-of-laws principles. Details of dispute resolution — including any right to opt out of arbitration — will appear here in the final version of this agreement.",
      },
    ],
  },
  "privacy-policy": {
    slug: "privacy-policy",
    eyebrow: "Legal",
    title: "Privacy policy",
    requirement:
      "App Store §5.1.1(i) — privacy practices disclosure. Regulation P (12 CFR §1016) — Financial Privacy Rule.",
    effective: "Effective date: to be set upon partner-bank agreement.",
    intro:
      "This policy explains what personal information AsterCash collects, why we collect it, who we share it with, and the rights you have over it.",
    sections: [
      {
        heading: "1. What we collect",
        body:
          "Information you provide (name, phone, email, date of birth, address, SSN or equivalent for identity verification); information about your use of the service (transactions, sign-in events, device metadata); information from third parties who help us verify identity or move money.",
      },
      {
        heading: "2. Why we collect it",
        body:
          "To provide the service (verifying identity, executing transfers, generating statements); to comply with financial-services regulation (BSA, KYC, sanctions screening, Reg E error resolution); to prevent fraud; to communicate with you about your account.",
      },
      {
        heading: "3. Who we share it with",
        body:
          "Our sponsor bank ([Sponsor Bank]); regulators and law enforcement when required by law; service providers under strict contractual confidentiality (identity verification, cloud hosting, fraud detection); nobody else for advertising or marketing purposes.",
      },
      {
        heading: "4. How long we keep it",
        body:
          "Transaction records: five years after the transaction (Bank Secrecy Act). Account records: five years after account closure. Support correspondence: two years. Nothing longer than legally required.",
      },
      {
        heading: "5. Your rights",
        body:
          "Under applicable privacy laws you may request a copy of your data (Settings → Data export request), request deletion (Settings → Data deletion request), correct inaccurate information, and opt out of certain uses. Contact privacy@astercash.com.",
      },
      {
        heading: "6. How we protect it",
        body:
          "All data in transit is encrypted with TLS 1.3. Data at rest is encrypted with AES-256. Access is limited to employees who need it for a specified job function, with audit logging on every access to sensitive systems.",
      },
      {
        heading: "7. Contact",
        body:
          "Privacy officer: privacy@astercash.com. For a written response by mail, request an address by email.",
      },
    ],
  },
  "terms-of-service": {
    slug: "terms-of-service",
    eyebrow: "Legal",
    title: "Terms of service",
    requirement: "Standard app agreement.",
    effective: "Effective date: to be set upon partner-bank agreement.",
    intro:
      "These Terms of Service are the contract between you and AsterCash for use of the AsterCash mobile app and website. They are separate from the prepaid account agreement (which governs the account itself, held at [Sponsor Bank]).",
    sections: [
      {
        heading: "1. What we agree to do",
        body:
          "Provide the AsterCash service in accordance with the descriptions on our website and in our mobile app. Maintain the service with reasonable uptime and security. Notify you of material changes at least 21 days in advance.",
      },
      {
        heading: "2. What you agree to do",
        body:
          "Provide accurate information at account opening. Use the service only for lawful purposes. Not attempt to circumvent security measures. Notify us promptly of unauthorised access.",
      },
      {
        heading: "3. Prohibited uses",
        body:
          "You may not use AsterCash for illegal activity, to launder money, to fund terrorism, to gamble in jurisdictions where prohibited, or to circumvent sanctions. We reserve the right to suspend accounts we reasonably believe are being used this way and to report as required by law.",
      },
      {
        heading: "4. Intellectual property",
        body:
          "The AsterCash brand, mobile app, and website are our intellectual property. You may use them only to interact with your own account.",
      },
      {
        heading: "5. Limitation of liability",
        body:
          "Detailed limitation-of-liability language will appear here in the final version. It will not attempt to disclaim liability we cannot legally disclaim, including for our own fraud, gross negligence, or violations of consumer-protection law.",
      },
      {
        heading: "6. Termination",
        body:
          "You may close your account at any time (Settings → Close account). We may terminate your access if you materially breach these terms, with notice except in cases involving fraud, illegal activity, or risk to other users.",
      },
      {
        heading: "7. Contact",
        body:
          "Legal: legal@astercash.com. Support: support@astercash.com.",
      },
    ],
  },
  "fee-schedule": {
    slug: "fee-schedule",
    eyebrow: "Legal",
    title: "Fee schedule (long form)",
    requirement:
      "CFPB Prepaid Accounts Rule (12 CFR §1005.18(b)(2)(xiv)) — long-form fee disclosure.",
    effective: "Effective date: to be set upon partner-bank agreement.",
    intro:
      "This is the complete list of every fee AsterCash may charge you. Any fee not listed here will not be charged. The short-form disclosure required by the CFPB Prepaid Accounts Rule is available at account opening and on the marketing site.",
    sections: [
      {
        heading: "Monthly maintenance",
        body: "None. There is no monthly fee to have an AsterCash account.",
      },
      {
        heading: "Peer-to-peer transfers to another AsterCash user",
        body: "None.",
      },
      {
        heading: "Receiving money",
        body: "None.",
      },
      {
        heading: "Adding money from a linked bank account (ACH)",
        body: "None. Standard ACH clearing time applies.",
      },
      {
        heading: "Adding money from a debit or credit card",
        body:
          "1.5% of the amount added, minimum $0.25. Passed through from the card network — no markup by AsterCash.",
      },
      {
        heading: "Instant withdrawal to a linked debit card",
        body:
          "1.5% of the amount withdrawn, minimum $0.25. Passed through from the card network.",
      },
      {
        heading: "Standard withdrawal to a linked bank account (ACH)",
        body: "None. Standard ACH clearing time applies.",
      },
      {
        heading: "ATM withdrawal (if a card is ever issued)",
        body:
          "To be determined when a card product is offered. This row will be updated with the exact fee 21 days before any card product launches.",
      },
      {
        heading: "Statement paper copy (by mail)",
        body:
          "None, if requested by email at support@astercash.com. Digital statements are always free.",
      },
      {
        heading: "Chargebacks and reversals",
        body: "None charged by AsterCash. Third-party fees may apply.",
      },
      {
        heading: "Foreign-currency transactions",
        body:
          "AsterCash does not currently support foreign-currency transactions. This section will be updated 21 days before that changes.",
      },
    ],
  },
  "reg-e-rights": {
    slug: "reg-e-rights",
    eyebrow: "Legal",
    title: "Your Reg E rights",
    requirement:
      "Regulation E (12 CFR §1005.7 & §1005.11) — consumer disclosures and error-resolution procedures.",
    effective: "Effective date: to be set upon partner-bank agreement.",
    intro:
      "Regulation E is a federal rule that protects you when there is an error on your account. This is a plain-language summary of your rights. The definitive text of Regulation E is available at consumerfinance.gov.",
    sections: [
      {
        heading: "What counts as an error",
        body:
          "An unauthorised electronic transfer; an incorrect amount; a transfer you did not receive that you were entitled to; a computational error by us; or missing information on your statement that we're required to include.",
      },
      {
        heading: "Your notice deadline",
        body:
          "You must notify us within 60 days of the date the statement showing the error was made available to you. This deadline preserves the strongest set of protections; contact us anyway if you're outside 60 days and we'll do what we can.",
      },
      {
        heading: "How to notify us",
        body:
          "Settings → Report a problem with a transaction, or email disputes@astercash.com. Include the transaction date, amount, counterparty, and a one-line description of the error.",
      },
      {
        heading: "What we'll do",
        body:
          "We will investigate. If the investigation takes more than 10 business days (up to 45 total) we will provisionally credit your account for the disputed amount so you have use of the money while we investigate.",
      },
      {
        heading: "Your liability for unauthorised transfers",
        body:
          "If you notify us within two business days of learning of the loss or theft, your liability is capped at $50. If you notify us within 60 days but more than two business days after learning, liability may be up to $500. Beyond 60 days, liability may be unlimited for transfers occurring after the 60-day window. Notify us as soon as possible.",
      },
      {
        heading: "Written explanation",
        body:
          "After we complete our investigation you will receive a written explanation of the outcome. If we determine no error occurred, you have the right to request copies of the documents we used to reach that decision.",
      },
      {
        heading: "Contact for Reg E disputes",
        body:
          "disputes@astercash.com — case number returned within one business day.",
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Not-found fallback.
// ---------------------------------------------------------------------------

function LegalNotFound({ slug }: { slug: string }) {
  return (
    <AppShell current="settings">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Legal</p>
          <h1 className={styles.pageTitle}>Document not found</h1>
        </div>
      </header>
      <p className={styles.leadCopy}>
        No legal document exists at <code>/legal/{slug}</code>. Return to{" "}
        <a href="/settings" className={styles.link}>
          Settings
        </a>{" "}
        for the full list.
      </p>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Legal() {
  const params = useParams();
  const slug = params.slug || "";
  const doc = DOCS[slug];

  if (!doc) return <LegalNotFound slug={slug} />;

  return (
    <AppShell current="settings">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>{doc.eyebrow}</p>
          <h1 className={styles.pageTitle}>{doc.title}</h1>
        </div>
      </header>

      <p className={styles.leadCopy}>{doc.intro}</p>

      <Card className={styles.metaCard}>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>Required by</span>
          <span className={styles.metaValue}>{doc.requirement}</span>
        </div>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>Version</span>
          <span className={styles.metaValue}>{doc.effective}</span>
        </div>
      </Card>

      <Card className={styles.draftBanner}>
        <p className={styles.draftTitle}>Draft — being finalised by counsel</p>
        <p className={styles.draftBody}>
          The section outline below is the structure of the finished document.
          Final language is being finalised by counsel with our sponsor bank.
          Until then, if you have a question, email{" "}
          <a href="mailto:legal@astercash.com" className={styles.link}>
            legal@astercash.com
          </a>{" "}
          — replies within one business day.
        </p>
      </Card>

      <article className={styles.doc}>
        {doc.sections.map((s, i) => (
          <section key={i} className={styles.section}>
            <h2 className={styles.sectionHeading}>{s.heading}</h2>
            <p className={styles.sectionBody}>{s.body}</p>
          </section>
        ))}
      </article>

      <footer className={styles.docFoot}>
        <p className={styles.docFootLine}>
          Return to{" "}
          <a href="/settings" className={styles.link}>
            Settings
          </a>{" "}
          for the full list of legal documents.
        </p>
      </footer>
    </AppShell>
  );
}
